// Simple helpers for JSON-encoded browser cookies with optional TTL.

/**
 * @typedef {(string|number|boolean|Object|null)} CookieValue
 */

/**
 * @typedef {Object} CookieOptions
 * @property {number} [expiresInMs]
 * @property {string} [path]
 * @property {string} [domain]
 * @property {boolean} [secure]
 * @property {('Strict'|'Lax'|'None')} [sameSite]
 */

const DEFAULT_SAMESITE = 'Lax';

/**
 * Sets a cookie with a JSON-serialized value and optional expiry/attributes.
 *
 * @param {string} key
 * @param {CookieValue} value
 * @param {CookieOptions} [options]
 */
export function setCookie(key, value, options = {}) {
  try {
    const {
      expiresInMs,
      path = '/',
      domain,
      secure,
      sameSite = DEFAULT_SAMESITE,
    } = options;

    const serializedValue = encodeURIComponent(JSON.stringify(value));
    let cookieString = `${encodeURIComponent(key)}=${serializedValue}`;

    if (expiresInMs) {
      const expiryDate = new Date(Date.now() + expiresInMs);
      cookieString += `; expires=${expiryDate.toUTCString()}`;
    }

    if (path) {
      cookieString += `; path=${path}`;
    }

    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    if (secure) {
      cookieString += '; Secure';
    }

    if (sameSite) {
      cookieString += `; SameSite=${sameSite}`;
    }

    document.cookie = cookieString;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error setting cookie "${key}":`, error);
  }
}

/**
 * Reads and parses a JSON-serialized cookie value.
 *
 * @param {string} key
 * @returns {CookieValue}
 */
export function getCookie(key) {
  try {
    if (!document.cookie) return null;

    const encodedKey = `${encodeURIComponent(key)}=`;
    const cookies = document.cookie.split(';');
    let rawValue = null;

    for (let i = 0; i < cookies.length; i += 1) {
      const cookie = cookies[i].trim();
      if (cookie.startsWith(encodedKey)) {
        rawValue = cookie.substring(encodedKey.length);
        break;
      }
    }

    if (rawValue === null) {
      return null;
    }

    const decoded = decodeURIComponent(rawValue);
    return JSON.parse(decoded);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error getting cookie "${key}":`, error);
    return null;
  }
}
